import { message } from "src/components/Message";
import { BBR_Bygning, BBR_Enhed, BBR_Grund, BBR_TekniskAnlæg } from "src/dataCollector/BBRTypes"
import { DataCollector } from "src/dataCollector/DataCollector"
import { EjendomEjerskab, EjendomTingbog, EjendomHandel } from "src/dataCollector/DLLTypes"
import { Mat2Feature } from "src/dataCollector/Mat2"
import { BBRKodeLister } from "src/propertyInfoTemplates/BBRKodeLister";
import * as XLSX from 'xlsx';
import { EBRAresseOpslag } from "src/dataCollector/EBRQGL_Lib";

type DataObject = Partial<{
   ejendom: Mat2Feature[],
   bygning: BBR_Bygning,
   ebr: EBRAresseOpslag[] // GraphQL structure
   enhed: BBR_Enhed,
   grund: BBR_Grund,
   tekniskanlaeg: BBR_TekniskAnlæg,
   ejerskab: (EjendomEjerskab[number] & { bfeNr: string })[],
   tingbog: EjendomTingbog,
   handel: EjendomHandel,
}>

// MARK: MAIN

export type PropertyInPut = { bfe: string, type: "SFE" | "EJL" | "BPFG" }
export async function PropertiesToExcel(properties: PropertyInPut[], options?: {
   ejendom?: boolean,
   adresser?: boolean,
   bbr?: boolean,
   jordstykker?: boolean,
   ejerskab?: boolean,
   tingbog?: boolean,
   handel?: boolean,
   ekstra?: boolean
}): Promise<XLSX.WorkBook> {
   let optionsDefault: Required<typeof options> = {
      ejendom: options?.ejendom ?? false,
      adresser: options?.adresser ?? false,
      bbr: options?.bbr ?? false,
      jordstykker: options?.jordstykker ?? false,
      ejerskab: options?.ejerskab ?? false,
      tingbog: options?.tingbog ?? false,
      handel: options?.handel ?? false,
      ekstra: options?.ekstra ?? false,
   }

   let data: DataObject = {}
   let sources: { Data: string, Kilde: string }[] = [];

   // MARK: FETCH DATA
   async function fetchData() {
      try {
         message.progress("Henter ejendomsdata...", 0);
         let list = Array.isArray(properties) ? properties : [properties];
         let segmenter = list.length / 100;
         for (let i = 0; i < segmenter; i++) {
            let segment = list.slice(i * 100, Math.min((i + 1) * 100, list.length));
            await DataCollector.MAT2.getEjendomme(segment.map((a) => a.bfe)).then((res) => {
               data.ejendom = [...data.ejendom || [], ...Object.values(res).filter((e): e is Mat2Feature => e !== undefined) as Mat2Feature[]]
            });
            if (optionsDefault?.adresser) {
               message.progress("Henter adressedata...", 0);
               const bfeNrs = segment.map((a) => a.bfe.toString());
               const batchResults = await DataCollector.DF_GQL.EBR.EBRAdresseOgPunkt(bfeNrs);
               data.ebr = [...data.ebr || [], ...batchResults]
            }
         }
         if (optionsDefault?.ejerskab) {
            message.progress("Henter ejerskabsdata...", 0);
            let idx = 0;
            for (const property of list) {
               idx++;
               message.progress("Henter ejerskabsdata...", idx / list.length * 100);
               let ejerskab = await DataCollector.DLL.EjendomEjerskab(property.bfe);
               data.ejerskab?.push(...ejerskab.map((e) => ({ ...e, bfeNr: property.bfe })));
            }
         }
         if (optionsDefault?.bbr) {
            data.bygning = [];
            data.enhed = [];
            data.grund = [];
            data.tekniskanlaeg = [];
            let idx = 0;
            for (const property of list) {
               idx++;
               message.progress("Henter bbrData...", idx / list.length * 100);
               let type = property.type;
               if (type === "SFE") {
                  let result = await DataCollector.BBR.getBBRData(property.bfe, type);
                  data.bygning?.push(...result.bygning);
                  data.enhed?.push(...result.enhed);
                  data.grund?.push(...result.grund);
                  data.tekniskanlaeg?.push(...result.tekniskanlaeg);
               } else {
                  let result = await DataCollector.BBR.getBBRData(property.bfe, type);
                  data.bygning?.push(...result.bygning)
                  data.enhed?.push(...result.enhed)
                  data.tekniskanlaeg?.push(...result.tekniskanlaeg)
               }
            };
         }
         message.progress("Ejendomsdata hentet", 100);
      } catch (e) {
         console.error("Error fetching property data", e);
         message.error("Der skete en fejl under hentning af ejendomsdata. Prøv igen senere.");
         message.progress();
      }

   }
   const workbook = XLSX.utils.book_new();
   await fetchData();

   if (data.ejendom?.length) {
      sources.push({ Data: "Ejendomme", Kilde: "Geodatastyrelsen, Matriklen" });
      let ejendomInfo = outPutEjendom(data.ejendom);
      let headerRow = ejendomHeaders;
      if (optionsDefault.adresser) {
         sources.push({ Data: "Adresser", Kilde: "Geodatastyrelsen, Danmarks Adresseregister" });
         let adresseInfo = outPutAdresse(data.ebr);
         ejendomInfo = joinBy("BFEnr", ejendomInfo, adresseInfo);
         headerRow = insertAfter("Type", headerRow, adresseHeaders);
      }
      if (optionsDefault.ejerskab) {
         sources.push({ Data: "Ejerskab", Kilde: "Geodatastyrelsen, Ejerfortegnelsen" });
         let ejerskabInfo = outPutEjerskab(data.ejerskab);
         ejendomInfo = joinBy("BFEnr", ejendomInfo, ejerskabInfo);
         headerRow = insertAfter("Type", headerRow, ejerskabHeaders);
      }

      let sheet = XLSX.utils.json_to_sheet(ejendomInfo, { header: headerRow });
      workbook.SheetNames.push("Ejendom");
      workbook.Sheets["Ejendom"] = sheet;
   }

   if (data.bygning?.length) {
      sources.push({ Data: "Bygninger", Kilde: "Vurderingsstyrelsen, Bygnings- og Boligregistret" });
      let bygningInfo = outPutBygning(data.bygning);
      let headerRow = bygningHeaders;
      workbook.SheetNames.push("Bygninger");
      workbook.Sheets["Bygninger"] = XLSX.utils.json_to_sheet(uniqueByKey("Id", bygningInfo), { header: headerRow });
   }

   if (data.enhed?.length) {
      sources.push({ Data: "Enheder", Kilde: "Vurderingsstyrelsen, Bygnings- og Boligregistret" });
      let enhedInfo = outPutEnhed(data.enhed);
      let headerRow = enhedHeaders;
      workbook.SheetNames.push("Enheder");
      workbook.Sheets["Enheder"] = XLSX.utils.json_to_sheet(uniqueByKey("Id", enhedInfo), { header: headerRow });
   }

   if (data.grund?.length) {
      sources.push({ Data: "Grunde", Kilde: "Vurderingsstyrelsen, Bygnings- og Boligregistret" });
      let grundInfo = outPutGrund(data.grund);
      let headerRow = grundHeaders;
      workbook.SheetNames.push("Grunde");
      workbook.Sheets["Grunde"] = XLSX.utils.json_to_sheet(uniqueByKey("Id", grundInfo), { header: headerRow });
   }

   if (data.tekniskanlaeg?.length) {
      sources.push({ Data: "Tekniske Anlæg", Kilde: "Vurderingsstyrelsen, Bygnings- og Boligregistret" });
      let tekniskanlaegInfo = outPutTekniskanlaeg(data.tekniskanlaeg);
      let headerRow = tekniskanlaegHeaders;
      workbook.SheetNames.push("Tekniske Anlæg");
      workbook.Sheets["Tekniske Anlæg"] = XLSX.utils.json_to_sheet(uniqueByKey("Id", tekniskanlaegInfo), { header: headerRow });
   }

   if (optionsDefault.jordstykker && data.ejendom?.filter((e) => e.type == "SFE").length) {
      sources.push({ Data: "Jordstykker", Kilde: "Geodatastyrelsen, Matriklen" });
      let sheet = XLSX.utils.json_to_sheet(outPutJordstykke(data.ejendom.filter((e) => e.type == "SFE")), { header: jordstykkeHeaders });
      workbook.SheetNames.push("Jordstykker");
      workbook.Sheets["Jordstykker"] = sheet;
   }



   if (sources.length) {
      let sheet = XLSX.utils.json_to_sheet(sources, { header: ["Data", "Kilde"] });
      workbook.SheetNames.push("Kilder");
      workbook.Sheets["Kilder"] = sheet;
   }
   message.progress();

   return workbook;
}

// MARK: OUTPUT FORMATTING
const ejerskabHeaders = ["BFEnr", "EjerListe"]
function outPutEjerskab(data: DataObject["ejerskab"]): { [key: string]: any }[] {
   if (!data) return [];
   return data.map((e) => ({
      BFEnr: e.bfeNr,
      EjerListe: e.displayText,
      // CVRnr: "Empty for now" // TODO: Find CVR number (not in current data from DLL,
   }));
}

const ejendomHeaders = ["BFEnr", "SFEnr", "Type", "Status", "Virkning fra", "Landbrugsnotering", "Er Udskilt Vej", "Opdelt i Ejerlejligheder", "Fælleslod", "Arbejderbolig", "Antal Jordstykker", "Antal BygningPåFremmedGrund", "Antal Ejerlejligheder"]
function outPutEjendom(data: DataObject["ejendom"]): { [key: typeof ejendomHeaders[number]]: any }[] {
   if (!data) return [];
   return data.map((e: Mat2Feature) => ({
      "BFEnr": e.BFEnummer,
      "SFEnr": parseInt((e.samletFastEjendomBFEnummer ?? e.BFEnummer) + ""),
      "Type": e.type,
      "Status": e.status,
      "Virkning fra": parseDate(e.virkningFra),
      "Landbrugsnotering": e.landbrugsnotering,
      "Er Udskilt Vej": parseBoolean(e.udskiltVej),
      "Opdelt i Ejerlejligheder": parseBoolean(e.hovedejendomOpdeltIEjerlejligheder),
      "Fælleslod": parseBoolean(e.erFaelleslod),
      "Arbejderbolig": parseBoolean(e.arbejderbolig),
      "Antal Jordstykker": e.type == "SFE" ? e.jordstykke?.length : 0,
      "Antal BygningPåFremmedGrund": e.type == "BPFG" ? 1 : e.bygningPaaFremmedGrund?.["length"] ?? 0,
      "Antal Ejerlejligheder": e.type == "EJL" ? 1 : e.ejerlejlighed.length ?? 0,
   }));
}

const adresseHeaders = ["BFEnr", "Adresse"]
function outPutAdresse(data: DataObject["ebr"]): { [key: typeof adresseHeaders[number]]: any }[] {
   if (!data || !Array.isArray(data)) return [];

   // Handle GraphQL structure: array of { EBR_Ejendomsbeliggenhed: { nodes: [...] } }
   return data.map((opslag) => {
      return {
         "BFEnr": opslag.bfeNr,
         "Adresse": opslag.betegnelse,
      }
   });
}

const jordstykkeHeaders = ["BFEnr", "MatrikelNr", "Ejerlav", "Ejerlavskode", "KommuneID", "Status", "Virkning fra", "Areal type", "Areal", "Vej areal"]
function outPutJordstykke(data: DataObject["ejendom"]): { [key: typeof jordstykkeHeaders[number]]: any }[] {
   return data?.filter((a) => a.type == "SFE").flatMap((e) => {
      return (e.jordstykke ?? []).map((p) => p.properties).map((j) => ({
         "BFEnr": j.samletFastEjendomBFEnummer,
         "MatrikelNr": j.matrikelnummer,
         "Ejerlav": j.ejerlavsnavn,
         "Ejerlavskode": j.ejerlavskode,
         "KommuneID": j.kommuneLokalId,
         "Status": j.status,
         "Virkning fra": parseDate(j.virkningFra),
         "Areal type": j.arealtype,
         "Areal": j.registreretAreal,
         "Vej areal": j.vejareal,

         // Jordrente
         // Klitfredning
         // Majoratsskov
         // Stormfald
         // Fredskov
      }));
   }) ?? [];
}

const bygningHeaders = ["Id", "Bygningsnummer", "Anvendelse", "Opførelsesår", "Ydervæggens Materiale", "Tagdækningsmateriale", "Samlet Bygningsareal", "Samlede Bolig Areal", "Samlede Erhvervs Areal", "Andet Areal", "Bebygget Areal", "Antal Etager", "Status", "Virkning Fra"]
function outPutBygning(data: DataObject["bygning"]): { [key: typeof bygningHeaders[number]]: any }[] {
   if (!data) return [];
   return data.map((b) => ({
      "Id": b.id_lokalId,
      "Bygningsnummer": b.byg007Bygningsnummer,
      "Anvendelse": BBRKodeLister.BygAnvendelse(b.byg021BygningensAnvendelse),
      "Opførelsesår": b.byg026Opførelsesår,
      "Ydervæggens Materiale": BBRKodeLister.YdervaeggenesMateriale(b.byg032YdervæggensMateriale),
      "Tagdækningsmateriale": BBRKodeLister.Tagdaekningsmateriale(b.byg033Tagdækningsmateriale),
      "Samlet Bygningsareal": b.byg038SamletBygningsareal,
      "Samlede Bolig Areal": b.byg039BygningensSamledeBoligAreal,
      "Samlede Erhvervs Areal": b.byg040BygningensSamledeErhvervsAreal,
      "Andet Areal": b.byg048AndetAreal,
      "Bebygget Areal": b.byg041BebyggetAreal,
      "Antal Etager": b.byg054AntalEtager,
      "Status": BBRKodeLister.Livcyklus(b.status),
      "Virkning Fra": parseDate(b.virkningFra),
   }));
}

const enhedHeaders = ["ID", "Anvendelse", "Boligtype", "Kondemneret Boligenhed", "Samlet Areal", "Samlet Bolig Areal", "Samlet Erhvervs Areal", "Antal Værelser", "Toiletforhold", "Badeforhold", "Køkkenforhold", "Udlejningsforhold", "Godkendt Tom Bolig", "Antal Vandskyllede Toiletter", "Antal Badeværelser", "Etage", "Opgang", "Status", "Virkning Fra", "Bygning"]
function outPutEnhed(data: DataObject["enhed"]): { [key: typeof enhedHeaders[number]]: any }[] {
   if (!data) return [];
   return data.map((e) => ({
      "ID": e.id_lokalId,
      "Anvendelse": BBRKodeLister.EnhAnvendelse(e.enh020EnhedensAnvendelse),
      "Boligtype": BBRKodeLister.Boligtype(e.enh023Boligtype),
      "Kondemneret Boligenhed": BBRKodeLister.KondemneretBoligenhed(e.enh024KondemneretBoligenhed),
      "Samlet Areal": e.enh026EnhedensSamledeAreal,
      "Samlet Bolig Areal": e.enh027ArealTilBeboelse,
      "Samlet Erhvervs Areal": e.enh028ArealTilErhverv,
      "Antal Værelser": e.enh031AntalVærelser,
      "Toiletforhold": BBRKodeLister.ToiletForhold(e.enh032Toiletforhold),
      "Badeforhold": BBRKodeLister.Badeforhold(e.enh033Badeforhold),
      "Køkkenforhold": BBRKodeLister.Køkkenforhold(e.enh034Køkkenforhold),
      "Udlejningsforhold": BBRKodeLister.Udlejningsforhold(e.enh045Udlejningsforhold),
      "Godkendt Tom Bolig": BBRKodeLister.GodkendtTomBolig(e.enh048GodkendtTomBolig),
      "Antal Vandskyllede Toiletter": e.enh065AntalVandskylledeToiletter,
      "Antal Badeværelser": e.enh066AntalBadeværelser,
      "Etage": e.etage,
      "Opgang": e.opgang,
      "Status": BBRKodeLister.Livcyklus(e.status),
      "Virkning Fra": parseDate(e.virkningFra),
      "Bygning": e.bygning,
   }));
}

const grundHeaders = ["Id", "Status", "Virkning Fra"]
function outPutGrund(data: DataObject["grund"]): { [key: typeof grundHeaders[number]]: any }[] {
   if (!data) return [];
   return data.map((g) => ({
      "Id": g.id_lokalId,
      "Status": BBRKodeLister.Livcyklus(g.status),
      "Virkning Fra": parseDate(g.virkningFra),
   }));
}
const tekniskanlaegHeaders = ["Id", "Type", "Status", "Virkning Fra"]
function outPutTekniskanlaeg(data: DataObject["tekniskanlaeg"]): { [key: typeof tekniskanlaegHeaders[number]]: any }[] {
   if (!data) return [];
   return data.map((t) => ({
      "Id": t.id_lokalId,
      "Type": t.tek033Type,
      "Status": BBRKodeLister.Livcyklus(t.status),
      "Virkning Fra": parseDate(t.virkningFra),
   }));
}

//MARK: JOIN and INSERT

function joinBy<T1, T2, K extends keyof T1 & keyof T2>(key: K, arr: T1[], arr2: T2[]) {
   return arr.map((a) => ({
      ...a,
      ...arr2.find((k) => k[key] == a[key] as unknown)
   }));
}

function uniqueByKey<T, K extends keyof T>(key: K, array: T[]) {
   const seen = new Set();
   return array.filter(item => {
      const value = item[key];
      if (seen.has(value)) {
         return false;
      }
      seen.add(value);
      return true;
   });
}

function insertAfter<T>(after: T, array: T[], insert: T[]) {
   const index = array.indexOf(after);
   if (index === -1) {
      return [...array, ...insert];
   }
   return [
      ...array.slice(0, index + 1),
      ...insert.slice(1), // Skip the first element to avoid duplication
      ...array.slice(index + 1)
   ];
}

//MARK:PARSERS

function parseDate(dateStr: unknown): string | undefined {
   if (typeof dateStr !== "string") return undefined;
   let date = dateStr ? new Date(dateStr) : undefined;
   if (date) {
      return date.toLocaleDateString("da-DK", {
         year: 'numeric',
         month: '2-digit',
         day: '2-digit'
      });
   }
   return dateStr;
}

function parseBoolean(value: unknown): string | undefined {
   if (value === undefined) return undefined;
   return value ? "Ja" : "Nej";
}
